import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { OnboardingLayout } from "@/components/layout/OnboardingLayout";
import { Logo } from "@/components/layout/Logo";
import { PrimaryButton, InputField, TextLink } from "@/components/fionova/Primitives";
import { onboardingState } from "@/lib/onboardingState";
import { useAuth } from "@/context/AuthContext";
import { afterJourneyAuth } from "@/lib/journey";
import { track, trackSessionOnce, useExitTracking } from "@/lib/analytics";
import { MarketingOptIn } from "@/components/fionova/MarketingOptIn";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const reasonFor = (status) =>
  status === 409 ? "duplicate_email"
  : status === 422 ? "validation"
  : status === 429 ? "rate_limited"
  : "network_or_server";

export default function BeginJourney() {
  const navigate = useNavigate();
  const { register, refresh } = useAuth();
  const anonId = (() => { try { return sessionStorage.getItem("fionova_anon_id"); } catch { return null; } })();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [showLogin, setShowLogin] = useState(false);
  const [optIn, setOptIn] = useState(false);
  const [busy, setBusy] = useState(false);
  const startedRef = useRef(false);
  const exitRef = useRef(null);
  exitRef.current = busy ? null : { stage: "registration", from_discovery: !!anonId };
  useExitTracking(exitRef);

  const emailValid = EMAIL_RE.test(form.email.trim());
  const pwValid = form.password.length >= 6;
  const canSubmit = emailValid && pwValid && !busy;
  const dirty = !!(form.name || form.email || form.password);

  // GA4: registration page viewed (once per session; distinct from page_view).
  useEffect(() => { trackSessionOnce("registration_page_viewed", "registration_page_viewed"); }, []);

  // Prevent accidental loss of entered info on tab close / refresh.
  useEffect(() => {
    if (!dirty) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const onField = (patch) => {
    if (!startedRef.current) {
      startedRef.current = true;
      trackSessionOnce("registration_form_started", "registration_form_started");
    }
    setForm((f) => ({ ...f, ...patch }));
  };

  const goBack = () => {
    if (dirty && !window.confirm("Leave this page? The details you've entered won't be saved.")) return;
    navigate(new URLSearchParams(window.location.search).get("next") === "journey" ? "/journey" : "/");
  };

  const start = async () => {
    setError(""); setShowLogin(false); setBusy(true);
    track("registration_submitted");
    try {
      await register({ email: form.email.trim(), password: form.password, name: form.name.trim(), anonId, marketingOptIn: optIn });
      onboardingState.setAccount({ name: form.name.trim(), email: form.email.trim() });
      if (anonId) {
        try { sessionStorage.removeItem("fionova_anon_id"); sessionStorage.removeItem("fionova_discovery"); sessionStorage.removeItem("fionova_discovery_id"); } catch {}
        exitRef.current = null;
        navigate("/discovery/reveal");
      } else if (new URLSearchParams(window.location.search).get("next") === "journey") {
        await afterJourneyAuth(navigate, refresh);
      } else navigate("/");
    } catch (e) {
      const status = e.status;
      track("registration_failed", { reason: reasonFor(status) });
      setError(e.message || "Could not create account.");
      if (status === 409) setShowLogin(true);
      setBusy(false);
    }
  };

  const disabledReason = busy ? "" :
    !form.email.trim() ? "Enter your email to continue." :
    !emailValid ? "Please enter a valid email address." :
    !form.password ? "Choose a password to continue." :
    !pwValid ? "Password must be at least 6 characters." : "";

  return (
    <OnboardingLayout immersive testId="page-begin" onBack={goBack}>
      <div className="flex flex-col items-center pt-2 text-center">
        <Logo size={72} />
        <h1 className="mt-8 text-3xl font-semibold tracking-tight text-foreground">
          {anonId ? "Unlock The Unsaid and your Emotional X-Ray" : "Begin your journey"}
        </h1>
        <p className="mt-3 text-sm font-medium text-primary" data-testid="begin-reassurance">
          Free to start • No credit card required • Your private space
        </p>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          {anonId
            ? "I've saved our place. Create your free account to see both — The Unsaid and your Emotional X-Ray — and keep them in your private space. They stay free, your name is optional, and there's no credit card required."
            : "Experience the full guided onboarding for free. A subscription is only needed for continued full access afterward."}
        </p>
      </div>

      {/* No native <form>: keeps tag auto form-capture from ever reading field values. */}
      <div
        className="mt-8 flex flex-col gap-4"
        data-testid="begin-fields"
        onKeyDown={(e) => { if (e.key === "Enter" && e.target.tagName === "INPUT") { e.preventDefault(); if (canSubmit) start(); } }}
      >
        <InputField
          label="Your name (optional)"
          testId="begin-name"
          type="text"
          autoComplete="name"
          value={form.name}
          onChange={(e) => onField({ name: e.target.value })}
        />
        <div>
          <InputField
            label="Email"
            testId="begin-email"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            placeholder="you@example.com"
            value={form.email}
            onChange={(e) => onField({ email: e.target.value })}
          />
          {form.email.trim() && !emailValid && (
            <p className="mt-1.5 text-xs text-amber-700" data-testid="begin-email-hint">
              Please enter a valid email address (e.g. you@example.com).
            </p>
          )}
        </div>
        <div>
          <InputField
            label="Password"
            testId="begin-password"
            type="password"
            autoComplete="new-password"
            placeholder="At least 6 characters"
            value={form.password}
            onChange={(e) => onField({ password: e.target.value })}
          />
          {form.password && !pwValid && (
            <p className="mt-1.5 text-xs text-amber-700" data-testid="begin-password-hint">
              Must be at least 6 characters ({form.password.length}/6).
            </p>
          )}
        </div>

        {error && (
          <div data-testid="begin-error" className="text-sm text-red-600">
            <p>{error}</p>
            {showLogin && (
              <p className="mt-1">
                <TextLink data-testid="begin-error-login" onClick={() => navigate(new URLSearchParams(window.location.search).get("next") === "journey" ? "/login?next=journey" : "/login")}>
                  Log in instead
                </TextLink>
                {" · "}
                <TextLink data-testid="begin-error-forgot" onClick={() => navigate("/forgot-password")}>
                  Forgot password?
                </TextLink>
              </p>
            )}
          </div>
        )}

        <MarketingOptIn checked={optIn} onChange={setOptIn} testId="begin-marketing" />
        <PrimaryButton data-testid="begin-create-account" className="fio-cta mt-2" onClick={() => canSubmit && start()} disabled={!canSubmit}>
          {busy ? "Creating your account…" : "Create account"}
        </PrimaryButton>

        {!canSubmit && disabledReason && (
          <p className="text-center text-xs text-muted-foreground" data-testid="begin-disabled-reason">
            {disabledReason}
          </p>
        )}

        <p className="mt-1 text-center text-xs leading-relaxed text-muted-foreground" data-testid="begin-legal">
          By continuing, you acknowledge our{" "}
          <a href="https://getfionova.com/terms" target="_blank" rel="noopener noreferrer"
            data-testid="begin-terms-link"
            className="font-medium text-foreground underline underline-offset-2 hover:text-primary">
            Terms of Service
          </a>{" "}
          and{" "}
          <a href="https://getfionova.com/privacy" target="_blank" rel="noopener noreferrer"
            data-testid="begin-privacy-link"
            className="font-medium text-foreground underline underline-offset-2 hover:text-primary">
            Privacy Policy
          </a>
          .
        </p>
      </div>

      <div className="mt-6 text-center">
        <TextLink data-testid="begin-to-login" onClick={() => navigate(new URLSearchParams(window.location.search).get("next") === "journey" ? "/login?next=journey" : "/login")}>
          Already have an account? Log in
        </TextLink>
      </div>
    </OnboardingLayout>
  );
}
